import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft,
  Save,
  Loader2,
  MapPin,
  DollarSign,
  Filter,
  Clock,
  Gauge,
  Trash2,
  Plus,
  BarChart3,
  History,
  Play,
  Pause
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'robots', label: 'Robots' },
];

const ROBOT_SUBCATEGORIES = [
  { value: 'cleaning_bots', label: 'Cleaning Bots' },
];

const createDefaultExternalDelivery = () => ({
  enabled: false,
  endpoint_url: '',
  auth_header: 'Authorization',
  auth_token: '',
  timeout_seconds: 10,
  max_retries: 3,
});

export default function EditCampaignPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [states, setStates] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [bidTypes, setBidTypes] = useState([]);
  const [campaign, setCampaign] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    lead_type: 'data_lead',
    bid_type: 'shared',
    max_bid: 0,
    states: [],
    budgets: [],
    schedule: { enabled: false, days: [], hours: { start: '09:00', end: '17:00' } },
    throttling: { enabled: false, max_leads_per_hour: 0, max_leads_per_day: 0 },
    lead_filters: { category: '', subcategory: '', business_type: '', industry: '' },
    external_delivery: createDefaultExternalDelivery(),
  });

  const updateLeadFilters = (updates) => {
    setFormData((prev) => ({
      ...prev,
      lead_filters: { ...prev.lead_filters, ...updates },
    }));
  };

  const updateExternalDelivery = (updates) => {
    setFormData((prev) => ({
      ...prev,
      external_delivery: { ...prev.external_delivery, ...updates },
    }));
  };

  const selectedCategory = formData.lead_filters.category || (formData.lead_type === 'robots' ? 'robots' : '');
  const selectedSubcategory = formData.lead_filters.subcategory;

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchData();
  }, [navigate, campaignId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [campaignRes, statesRes, leadTypesRes, bidTypesRes] = await Promise.all([
        axios.get(`${API_URL}/api/client-portal/campaigns/${campaignId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/campaigns/reference/states`),
        axios.get(`${API_URL}/api/campaigns/reference/lead-types`),
        axios.get(`${API_URL}/api/campaigns/reference/bid-types`)
      ]);
      
      const campaignData = campaignRes.data;
      setCampaign(campaignData);
      setStates(statesRes.data);
      setLeadTypes(leadTypesRes.data);
      setBidTypes(bidTypesRes.data);
      
      setFormData({
        name: campaignData.name || '',
        description: campaignData.description || '',
        lead_type: campaignData.lead_type || 'data_lead',
        bid_type: campaignData.bid_type || 'shared',
        max_bid: campaignData.max_bid || 0,
        states: campaignData.states || [],
        budgets: campaignData.budgets || [],
        schedule: campaignData.schedule || { enabled: false, days: [], hours: { start: '09:00', end: '17:00' } },
        throttling: campaignData.throttling || { enabled: false, max_leads_per_hour: 0, max_leads_per_day: 0 },
        lead_filters: { category: '', subcategory: '', business_type: '', industry: '', ...(campaignData.lead_filters || {}) },
        external_delivery: { ...createDefaultExternalDelivery(), ...(campaignData.external_delivery || {}) },
      });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Campaign not found');
        navigate('/client-portal/campaigns');
      } else {
        toast.error('Failed to load campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/client-portal/campaigns/${campaignId}`,
        formData,
        { headers: getAuthHeaders() }
      );
      toast.success('Campaign updated successfully');
      navigate('/client-portal/campaigns');
    } catch (error) {
      toast.error('Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/client-portal/campaigns/${campaignId}/toggle`,
        { is_active: !campaign.is_active },
        { headers: getAuthHeaders() }
      );
      setCampaign({ ...campaign, is_active: response.data.is_active });
      toast.success(`Campaign ${response.data.is_active ? 'activated' : 'paused'}`);
    } catch (error) {
      toast.error('Failed to toggle campaign');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/client-portal/campaigns/${campaignId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Campaign deleted');
      navigate('/client-portal/campaigns');
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const toggleState = (stateCode) => {
    if (formData.states.includes(stateCode)) {
      setFormData({ ...formData, states: formData.states.filter(s => s !== stateCode) });
    } else {
      setFormData({ ...formData, states: [...formData.states, stateCode] });
    }
  };

  const selectAllStates = () => setFormData({ ...formData, states: states.map(s => s.code) });
  const selectNoStates = () => setFormData({ ...formData, states: [] });

  const addBudget = () => {
    setFormData({
      ...formData,
      budgets: [...formData.budgets, { type: 'daily', limit_type: 'spend', amount: 0 }]
    });
  };

  const updateBudget = (index, field, value) => {
    const newBudgets = [...formData.budgets];
    newBudgets[index] = { ...newBudgets[index], [field]: value };
    setFormData({ ...formData, budgets: newBudgets });
  };

  const removeBudget = (index) => {
    setFormData({ ...formData, budgets: formData.budgets.filter((_, i) => i !== index) });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="edit-campaign-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/client-portal/campaigns')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold">Edit Campaign</h1>
                <Badge variant={campaign?.is_active ? 'default' : 'secondary'}>
                  {campaign?.is_active ? 'Active' : 'Paused'}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{campaign?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate(`/client-portal/campaigns/${campaignId}/history`)}>
              <History className="w-4 h-4 mr-2" />
              View History
            </Button>
            <Button
              variant="outline"
              onClick={handleToggle}
              className={campaign?.is_active ? 'text-orange-600' : 'text-green-600'}
            >
              {campaign?.is_active ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              {campaign?.is_active ? 'Pause' : 'Activate'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/client-portal/campaigns')}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 gap-8">
          {/* Left Column - Settings */}
          <div className="col-span-2 space-y-6">
            <Accordion type="multiple" defaultValue={['bid-budgets', 'location', 'filters']} className="space-y-4">
              {/* Bid and Budgets */}
              <AccordionItem value="bid-budgets" className="bg-white rounded-lg border">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                    <span className="font-medium">Bid and Budgets</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-6">
                  <div className="border-l-4 border-teal-500 pl-4 space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Maximum Bid</Label>
                      <p className="text-xs text-muted-foreground mb-2">This is the highest price this campaign will pay for a lead</p>
                      <div className="flex items-center gap-2">
                        <span>Maximum Bid: $</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.max_bid}
                          onChange={(e) => setFormData({ ...formData, max_bid: parseFloat(e.target.value) || 0 })}
                          className="w-32"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4">
                    <Label className="text-sm font-medium">Budgets</Label>
                    <p className="text-xs text-muted-foreground mb-4">Set a budget based on spend or lead count.</p>
                    
                    {formData.budgets.map((budget, index) => (
                      <div key={index} className="flex items-center gap-2 mb-3 p-3 bg-muted/50 rounded">
                        <Switch checked={true} />
                        <span>Limit</span>
                        <Select value={budget.limit_type} onValueChange={(v) => updateBudget(index, 'limit_type', v)}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="spend">spend</SelectItem>
                            <SelectItem value="leads">leads</SelectItem>
                          </SelectContent>
                        </Select>
                        <span>to</span>
                        <Input type="number" value={budget.amount} onChange={(e) => updateBudget(index, 'amount', parseFloat(e.target.value) || 0)} className="w-24" />
                        <span>per</span>
                        <Select value={budget.type} onValueChange={(v) => updateBudget(index, 'type', v)}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">day</SelectItem>
                            <SelectItem value="weekly">week</SelectItem>
                            <SelectItem value="monthly">month</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" onClick={() => removeBudget(index)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                    
                    <Button variant="outline" onClick={addBudget} className="mt-2 text-teal-600">
                      <Plus className="w-4 h-4 mr-2" />Add Budget
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Location Targeting */}
              <AccordionItem value="location" className="bg-white rounded-lg border">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" />
                    <span className="font-medium">Location Targeting</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <p className="text-sm text-muted-foreground">Target your campaign by state.</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Total selected states: {formData.states.length}</div>
                    <div className="flex gap-2">
                      <Button variant="link" onClick={selectAllStates} className="text-teal-600 p-0">Select All</Button>
                      <span>|</span>
                      <Button variant="link" onClick={selectNoStates} className="text-teal-600 p-0">Select None</Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto border rounded-lg p-4">
                    {states.map((state) => (
                      <div key={state.code} className="flex items-center gap-2">
                        <Checkbox
                          id={`state-${state.code}`}
                          checked={formData.states.includes(state.code)}
                          onCheckedChange={() => toggleState(state.code)}
                        />
                        <label htmlFor={`state-${state.code}`} className="text-sm cursor-pointer">{state.name}</label>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Schedule */}
              <AccordionItem value="schedule" className="bg-white rounded-lg border">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-600" />
                    <span className="font-medium">Schedule</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.schedule.enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, schedule: { ...formData.schedule, enabled: checked } })}
                    />
                    <Label>Enable schedule restrictions</Label>
                  </div>
                  
                  {formData.schedule.enabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-teal-500">
                      <div className="flex items-center gap-4">
                        <div>
                          <Label>Start Time</Label>
                          <Input type="time" value={formData.schedule.hours.start} onChange={(e) => setFormData({ ...formData, schedule: { ...formData.schedule, hours: { ...formData.schedule.hours, start: e.target.value } } })} />
                        </div>
                        <div>
                          <Label>End Time</Label>
                          <Input type="time" value={formData.schedule.hours.end} onChange={(e) => setFormData({ ...formData, schedule: { ...formData.schedule, hours: { ...formData.schedule.hours, end: e.target.value } } })} />
                        </div>
                      </div>
                      <div>
                        <Label>Active Days</Label>
                        <div className="flex gap-2 mt-2">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                            <Button
                              key={day}
                              variant={formData.schedule.days.includes(day) ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => {
                                const days = formData.schedule.days.includes(day)
                                  ? formData.schedule.days.filter(d => d !== day)
                                  : [...formData.schedule.days, day];
                                setFormData({ ...formData, schedule: { ...formData.schedule, days } });
                              }}
                            >{day}</Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>

              {/* Throttling */}
              <AccordionItem value="throttling" className="bg-white rounded-lg border">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-teal-600" />
                    <span className="font-medium">Purchase Throttling</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.throttling.enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, throttling: { ...formData.throttling, enabled: checked } })}
                    />
                    <Label>Enable throttling</Label>
                  </div>
                  
                  {formData.throttling.enabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-teal-500">
                      <div>
                        <Label>Max Leads Per Hour</Label>
                        <Input type="number" min="0" value={formData.throttling.max_leads_per_hour} onChange={(e) => setFormData({ ...formData, throttling: { ...formData.throttling, max_leads_per_hour: parseInt(e.target.value) || 0 } })} className="w-32" />
                      </div>
                      <div>
                        <Label>Max Leads Per Day</Label>
                        <Input type="number" min="0" value={formData.throttling.max_leads_per_day} onChange={(e) => setFormData({ ...formData, throttling: { ...formData.throttling, max_leads_per_day: parseInt(e.target.value) || 0 } })} className="w-32" />
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="filters" className="bg-white rounded-lg border">
                <AccordionTrigger className="px-6 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-teal-600" />
                    <span className="font-medium">Lead Attribute Filtering</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Lead Category</Label>
                      <Select
                        value={selectedCategory || 'all'}
                        onValueChange={(value) => updateLeadFilters({ category: value === 'all' ? '' : value, subcategory: value === 'robots' ? formData.lead_filters.subcategory || 'cleaning_bots' : '' })}
                      >
                        <SelectTrigger className="mt-2" data-testid="edit-campaign-category-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {LEAD_CATEGORIES.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Robot Subcategory</Label>
                      <Select
                        value={selectedSubcategory || 'all'}
                        onValueChange={(value) => updateLeadFilters({ subcategory: value === 'all' ? '' : value })}
                        disabled={selectedCategory !== 'robots'}
                      >
                        <SelectTrigger className="mt-2" data-testid="edit-campaign-subcategory-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Subcategories</SelectItem>
                          {ROBOT_SUBCATEGORIES.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Business Type Filter</Label>
                      <Input
                        value={formData.lead_filters.business_type || ''}
                        onChange={(e) => updateLeadFilters({ business_type: e.target.value })}
                        placeholder="e.g. cleaning bots"
                        className="mt-2"
                        data-testid="edit-campaign-business-type-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Industry Filter</Label>
                      <Input
                        value={formData.lead_filters.industry || ''}
                        onChange={(e) => updateLeadFilters({ industry: e.target.value })}
                        placeholder="e.g. robotics"
                        className="mt-2"
                        data-testid="edit-campaign-industry-input"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border p-4 space-y-4 bg-muted/30">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Label className="text-sm font-medium">External Stack API Delivery</Label>
                        <p className="text-xs text-muted-foreground mt-1">Forward purchased leads from this campaign to another stack app.</p>
                      </div>
                      <Switch
                        checked={formData.external_delivery.enabled}
                        onCheckedChange={(checked) => updateExternalDelivery({ enabled: checked })}
                        data-testid="edit-campaign-external-delivery-toggle"
                      />
                    </div>

                    {formData.external_delivery.enabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label>Endpoint URL</Label>
                          <Input
                            value={formData.external_delivery.endpoint_url}
                            onChange={(e) => updateExternalDelivery({ endpoint_url: e.target.value })}
                            className="mt-2"
                            data-testid="edit-campaign-external-endpoint-input"
                          />
                        </div>
                        <div>
                          <Label>Auth Header</Label>
                          <Input
                            value={formData.external_delivery.auth_header}
                            onChange={(e) => updateExternalDelivery({ auth_header: e.target.value || 'Authorization' })}
                            className="mt-2"
                            data-testid="edit-campaign-external-auth-header-input"
                          />
                        </div>
                        <div>
                          <Label>Auth Token</Label>
                          <Input
                            type="password"
                            value={formData.external_delivery.auth_token}
                            onChange={(e) => updateExternalDelivery({ auth_token: e.target.value })}
                            className="mt-2"
                            data-testid="edit-campaign-external-auth-token-input"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600">Danger Zone</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">Once you delete a campaign, there is no going back.</p>
                <Button variant="destructive" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Campaign
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            <Card className="sticky top-24">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Campaign Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">CAMPAIGN NAME:</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" />
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">DESCRIPTION:</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1" rows={3} />
                </div>
                
                <div className="border-t pt-4">
                  <Label className="text-xs text-muted-foreground">BID RANGE</Label>
                  <div className="text-3xl font-bold text-teal-600">{formatCurrency(formData.max_bid)}</div>
                </div>
                
                <div className="space-y-3 text-sm border-t pt-4">
                  <div className="flex justify-between"><span>Category</span><span>{selectedCategory ? selectedCategory.replace(/_/g, ' ') : 'All'}</span></div>
                  <div className="flex justify-between"><span>Subcategory</span><span>{selectedSubcategory ? selectedSubcategory.replace(/_/g, ' ') : 'Any'}</span></div>
                  <div className="flex justify-between items-center">
                    <span>Product</span>
                    <Select value={formData.lead_type} onValueChange={(v) => setFormData({ ...formData, lead_type: v })}>
                      <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {leadTypes.map(lt => (<SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Bid Type</span>
                    <Select value={formData.bid_type} onValueChange={(v) => setFormData({ ...formData, bid_type: v })}>
                      <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {bidTypes.map(bt => (<SelectItem key={bt.value} value={bt.value}>{bt.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-between"><span>States</span><span>{formData.states.length} selected</span></div>
                  <div className="flex justify-between"><span>Status</span><Badge variant={campaign?.is_active ? 'default' : 'secondary'}>{campaign?.is_active ? 'Active' : 'Paused'}</Badge></div>
                </div>

                {/* Stats */}
                <div className="border-t pt-4 space-y-2">
                  <Label className="text-xs text-muted-foreground">CAMPAIGN STATS</Label>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-muted/50 rounded">
                      <div className="text-lg font-bold">{campaign?.stats?.leads || 0}</div>
                      <div className="text-xs text-muted-foreground">Leads</div>
                    </div>
                    <div className="p-2 bg-muted/50 rounded">
                      <div className="text-lg font-bold">{formatCurrency(campaign?.stats?.spend || 0)}</div>
                      <div className="text-xs text-muted-foreground">Spend</div>
                    </div>
                    <div className="p-2 bg-muted/50 rounded">
                      <div className="text-lg font-bold">{formatCurrency(campaign?.stats?.cpl || 0)}</div>
                      <div className="text-xs text-muted-foreground">CPL</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
